import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { City, initialCity } from '../models/city'
import { createCity, deleteCity, getCities, updateCity, getCity } from '../api/citiesApi'
import { KTSVG } from '../../../../../_dashboard/helpers/components/KTSVG'
import { toast } from 'react-toastify'
import { Tooltip, OverlayTrigger, Form } from 'react-bootstrap'

const CitiesTab: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [cities, setCities] = useState<City[]>([])
  const [editingCity, setEditingCity] = useState<City | null>(null)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [cityToDelete, setCityToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const pageSize = 1000

  const loadCities = async () => {
    try {
      setLoading(true)
      const response = await getCities(page, pageSize)
      setCities(response.data || [])
      setTotalPages(response.payload.pagination.totalPages || 0)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CITY.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCities()
  }, [page])

  const citySchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    country: Yup.string().required('Country is required'),
    region: Yup.string().required('Region is required'),
    description: Yup.string().required('Description is required'),
    status: Yup.string().oneOf(['active', 'inactive']).required('Status is required'),
  })

  const formik = useFormik({
    initialValues: editingCity || initialCity,
    validationSchema: citySchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (editingCity?._id) {
          await updateCity(editingCity._id, values)
          toast.success(intl.formatMessage({ id: 'TOUR_CITY.SUCCESS.UPDATE' }))
        } else {
          await createCity(values)
          toast.success(intl.formatMessage({ id: 'TOUR_CITY.SUCCESS.CREATE' }))
        }
        resetForm()
        setEditingCity(null)
        setShowModal(false)
        loadCities()
      } catch (error) {
        toast.error(intl.formatMessage({ id: editingCity?._id ? 'TOUR_CITY.ERROR.UPDATE' : 'TOUR_CITY.ERROR.CREATE' }))
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleDelete = async () => {
    if (!cityToDelete) return

    try {
      setIsDeleting(true)
      await deleteCity(cityToDelete)
      toast.success(intl.formatMessage({ id: 'TOUR_CITY.SUCCESS.DELETE' }))
      loadCities()
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CITY.ERROR.DELETE' }))
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
      setCityToDelete(null)
    }
  }

  const handleEdit = async (city: City) => {
    try {
      setLoading(true)
      const response = await getCity(city._id!)
      setEditingCity(response.data)
      formik.setValues(response.data)
      setShowModal(true)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_CITY.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditingCity(null)
    formik.resetForm()
    setShowModal(false)
  }

  const handleAddNew = () => {
    setEditingCity(null)
    formik.resetForm()
    setShowModal(true)
  }

  return (
    <div className='card card-custom'>
      {/* Header with Add Button */}
      <div className='card-header border-0 pt-6'>
        <div className='card-title'></div>
        <div className='card-toolbar'>
          <div className='d-flex justify-content-end'>
            <button type='button' className='btn btn-primary' onClick={handleAddNew}>
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
              {intl.formatMessage({ id: 'TOUR_CITY.ADD_NEW' })}
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className='card-body pt-0'>
        {loading ? (
          <div className='d-flex justify-content-center align-items-center min-h-300px'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className='table-responsive'>
              <table className='table align-middle table-row-dashed fs-6 gy-5'>
                <thead>
                  <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                    <th>{intl.formatMessage({ id: 'TOUR_CITY.NAME' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CITY.COUNTRY' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CITY.REGION' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CITY.STATUS' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_CITY.ACTIONS' })}</th>
                  </tr>
                </thead>
                <tbody className='text-gray-600 fw-semibold'>
                  {cities.map((city) => (
                    <tr key={city._id}>
                      <td>{city.name}</td>
                      <td>{city.country}</td>
                      <td>{city.region}</td>
                      <td>
                        <span
                          className={`badge badge-light-${
                            city.status === 'active' ? 'success' : 'danger'
                          } fw-bold`}
                        >
                          {city.status}
                        </span>
                      </td>
                      <td>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.EDIT'})}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-primary btn-sm me-1'
                            onClick={() => handleEdit(city)}
                          >
                            <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.DELETE'})}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-danger btn-sm'
                            onClick={() => {
                              if (city._id) {
                                setCityToDelete(city._id)
                                setShowDeleteConfirm(true)
                              }
                            }}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className='d-flex justify-content-between align-items-center flex-wrap'>
                <div className='d-flex flex-wrap py-2 mr-3'>
                  <button
                    className='btn btn-light btn-active-light-primary me-2'
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.PREVIOUS' })}
                  </button>
                  <button
                    className='btn btn-light btn-active-light-primary'
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.NEXT' })}
                  </button>
                </div>
                <div className='d-flex align-items-center py-3'>
                  <span className='text-muted'>
                    {intl.formatMessage(
                      { id: 'PAGINATION.INFO' },
                      {
                        page: page + 1,
                        total: totalPages,
                      }
                    )}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showModal && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 1050}}
        >
          <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable mw-650px'>
            <div className='modal-content border-0 rounded-4 shadow-lg'>
              <div className='modal-header border-0 pt-6 px-8 pb-2'>
                <h5 className='modal-title fs-3 fw-bold text-gray-900'>
                  {editingCity ? intl.formatMessage({id: 'TOUR_CITY.EDIT'}) : intl.formatMessage({id: 'TOUR_CITY.ADD_NEW'})}
                </h5>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={handleCancel}
                  disabled={formik.isSubmitting}
                >
                  <i className='bi bi-x-lg fs-3' />
                </button>
              </div>
              <form onSubmit={formik.handleSubmit}>
                <div className='modal-body px-8 py-6'>
                  <div className='row mb-6'>
                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>{intl.formatMessage({ id: 'TOUR_CITY.NAME' })}</label>
                        <input
                          type='text'
                          className={`form-control ${
                            formik.touched.name && formik.errors.name ? 'is-invalid' : ''
                          }`}
                          placeholder={intl.formatMessage({ id: 'TOUR_CITY.NAME_PLACEHOLDER' })}
                          {...formik.getFieldProps('name')}
                        />
                        {formik.touched.name && formik.errors.name && (
                          <div className='invalid-feedback'>{formik.errors.name}</div>
                        )}
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>{intl.formatMessage({ id: 'TOUR_CITY.STATUS' })}</label>
                        <div className='form-check form-switch form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            checked={formik.values.status === 'active'}
                            onChange={(e) => {
                              formik.setFieldValue('status', e.target.checked ? 'active' : 'inactive')
                            }}
                            id='statusSwitch'
                          />
                          <label className='form-check-label' htmlFor='statusSwitch'>
                            {intl.formatMessage({ id: formik.values.status === 'active' ? 'STATUS.ACTIVE' : 'STATUS.INACTIVE' })}
                          </label>
                        </div>
                        {formik.touched.status && formik.errors.status && (
                          <div className='invalid-feedback d-block'>{formik.errors.status}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>{intl.formatMessage({ id: 'TOUR_CITY.COUNTRY' })}</label>
                        <input
                          type='text'
                          className={`form-control ${
                            formik.touched.country && formik.errors.country ? 'is-invalid' : ''
                          }`}
                          placeholder={intl.formatMessage({ id: 'TOUR_CITY.COUNTRY_PLACEHOLDER' })}
                          {...formik.getFieldProps('country')}
                        />
                        {formik.touched.country && formik.errors.country && (
                          <div className='invalid-feedback'>{formik.errors.country}</div>
                        )}
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>{intl.formatMessage({ id: 'TOUR_CITY.REGION' })}</label>
                        <input
                          type='text'
                          className={`form-control ${
                            formik.touched.region && formik.errors.region ? 'is-invalid' : ''
                          }`}
                          placeholder={intl.formatMessage({ id: 'TOUR_CITY.REGION_PLACEHOLDER' })}
                          {...formik.getFieldProps('region')}
                        />
                        {formik.touched.region && formik.errors.region && (
                          <div className='invalid-feedback'>{formik.errors.region}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <div className='col-lg-12'>
                      <div className='form-group'>
                        <label className='form-label required'>{intl.formatMessage({ id: 'TOUR_CITY.DESCRIPTION' })}</label>
                        <textarea
                          className={`form-control ${
                            formik.touched.description && formik.errors.description ? 'is-invalid' : ''
                          }`}
                          rows={3}
                          placeholder={intl.formatMessage({ id: 'TOUR_CITY.DESCRIPTION_PLACEHOLDER' })}
                          {...formik.getFieldProps('description')}
                        />
                        {formik.touched.description && formik.errors.description && (
                          <div className='invalid-feedback'>{formik.errors.description}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                  <button
                    type='button'
                    className='btn btn-light-primary fw-semibold px-6'
                    onClick={handleCancel}
                    disabled={formik.isSubmitting}
                  >
                    {intl.formatMessage({id: 'ACTION.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary fw-semibold px-8'
                    disabled={formik.isSubmitting || !formik.isValid}
                  >
                    {formik.isSubmitting ? (
                      <>
                        <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                        <span className='ms-2'>{intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}</span>
                      </>
                    ) : (
                      intl.formatMessage({id: editingCity ? 'ACTION.UPDATE' : 'ACTION.SAVE'})
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 1050}}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content border-0 rounded-4 shadow-lg'>
              <div className='modal-header border-0 pt-6 px-8 pb-2'>
                <h5 className='modal-title fs-3 fw-bold text-gray-900'>{intl.formatMessage({id: 'HEADER.DELETE'})}</h5>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setCityToDelete(null)
                  }}
                  disabled={isDeleting}
                >
                  <i className='bi bi-x-lg fs-3' />
                </button>
              </div>
              <div className='modal-body px-8 py-6'>
                {intl.formatMessage({id: 'TOUR_CITY.DELETE_CONFIRM'})}
              </div>
              <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                <button
                  type='button'
                  className='btn btn-light-primary fw-semibold px-6'
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setCityToDelete(null)
                  }}
                  disabled={isDeleting}
                >
                  {intl.formatMessage({id: 'ACTION.CANCEL'})}
                </button>
                <button 
                  type='button' 
                  className='btn btn-danger fw-semibold px-6' 
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                      <span className='ms-2'>{intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}</span>
                    </>
                  ) : (
                    intl.formatMessage({id: 'ACTION.DELETE'})
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CitiesTab 